/**
 * Hover prefetch hook for list→detail navigation.
 *
 * Fires a tRPC query when the user hovers a list item, so data
 * is already cached when they click through to the detail page.
 *
 * Usage:
 *   const prefetchHandlers = usePrefetchOnHover(() =>
 *     utils.source.getById.prefetch({ id: source.id })
 *   );
 *
 *   <AriaGridListItem {...prefetchHandlers}>
 *
 * The prefetch only fires once per mount (not on every hover).
 * Uses a 100ms delay to avoid prefetching on accidental mouse-overs.
 */

import { useCallback, useEffect, useRef } from "react";

export function usePrefetchOnHover(prefetchFn: () => void) {
  const hasPrefetched = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up on unmount — prevent stale prefetch after navigation
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const onMouseEnter = useCallback(() => {
    if (hasPrefetched.current) return;
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      hasPrefetched.current = true;
      prefetchFn();
    }, 100);
  }, [prefetchFn]);

  const onMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return { onMouseEnter, onMouseLeave };
}
